import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { getKioskAuthorization, kioskPaymentStop } from 'api/Kiosk';

import { socket, socketEmit, socketOff, socketOn } from 'lib/clients/socket';
import { inRn, snakeToTitleCase } from 'lib/helpers/utility';
import useError from 'lib/hooks/useError';

import ErrorPaymentModal from 'components/Payments/NewPayment/PaymentContent/components/ErrorModal';
import { usePaymentContext } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';
import Loader from 'components/shared/Loader/Loader';
import Modal from 'components/shared/Modal/Modal';

import CardContent from './components/CardContent';
import Footer from './components/Footer';
import Header from './components/Header';
import Information from './components/Information';

const PayFromKioskModal = ({ isOpen, handleClose, patientId, onSuccess, token }) => {
  const [paymentKind, setPaymentKind] = useState('saved-cards');
  const [cardMessage, setCardMessage] = useState('');

  const { values, terminalData } = usePaymentContext();

  const scrollRef = useRef(null);
  const { error, setErrorMessage } = useError();
  const navigate = useNavigate();

  const timeoutRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ['kiosk-authorization', patientId],
    queryFn: () =>
      getKioskAuthorization(navigate, {
        patientId,
        balance: inRn(values?.amount),
        token: values?.invoiceToken || token,
        processor: terminalData?.cardPresentTerminal?.processor
      }),
    enabled: isOpen,
    refetchOnMount: 'always',
    staleTime: 0
  });

  const clearTimeoutHandler = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const closeModal = () => {
    handleClose();
    setPaymentKind('saved-cards');
    setErrorMessage(null);
    clearTimeoutHandler();
  };

  const goBack = () => {
    if (paymentKind === 'credit-card') {
      setPaymentKind('saved-cards');
      return;
    }

    closeModal();
  };

  const initSocket = (kAuth) => {
    try {
      if (!kAuth || kAuth === 'not_configured') {
        return;
      }

      socketOn('kioskPaymentScreenMessage', (data) => {
        switch (data?.kind) {
          case 'handlePaymentConfirmationReceived':
            clearTimeoutHandler();
            break;
          case 'switchToAlternative':
            setPaymentKind('credit-card');
            break;
          case 'success':
            clearTimeoutHandler();
            onSuccess(data?.transactionId);
            break;
          case 'handleCardMessageUpdate':
            setCardMessage(snakeToTitleCase(data?.message.toLowerCase()));
            break;
          case 'error':
            if (data?.code === 0) {
              setPaymentKind('credit-card');
            } else {
              setErrorMessage('Terminal not connected, please try again.');
            }
            break;
          default:
            if (data?.transactionId && !data?.message) {
              onSuccess(data?.transactionId);
              clearTimeoutHandler();
            }
            break;
        }
      });

      socketEmit('kioskJoinClient', { kioskAuthorization: kAuth }, { reemitOnReconnect: true });

      timeoutRef.current = setTimeout(async () => {
        const data = await kioskPaymentStop(navigate, {});
        if (data?.transaction?.status) {
          if (data?.transaction?.status === 'completed') {
            clearTimeoutHandler();
            onSuccess(data?.transaction?.id);
          } else {
            if (data?.message && data?.message?.includes('TransactionCardReaderNoneAvailable')) {
              setPaymentKind('credit-card');
            } else {
              setErrorMessage(data?.message);
            }
          }
        } else {
          setErrorMessage('Terminal not connected, please try again.');
        }
      }, 60000);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    initSocket(data?.kioskAuthorization);
    return () => {
      socketOff('kioskPaymentScreenMessage');
      clearTimeoutHandler();
    };
  }, [data?.kioskAuthorization]);

  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }
  }, [socket.connected]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        handleClose={closeModal}
        width="full"
        title={<Header goBack={goBack} step={paymentKind} />}
        footer={<Footer step={paymentKind} amount={values.amount} onClose={goBack} />}>
        <div className="flex h-full flex-col items-center justify-center">
          <div
            className="flex w-full max-w-[448px] flex-col items-center gap-6 px-4 pb-6 pt-4"
            ref={scrollRef}>
            <Information paymentKind={paymentKind} cardMessage={cardMessage} />
            {isLoading ? (
              <Loader color="primary" size={40} />
            ) : (
              <CardContent
                ref={scrollRef}
                active={paymentKind}
                setActive={setPaymentKind}
                setErrorMessage={setErrorMessage}
                patientId={patientId}
              />
            )}
          </div>
        </div>
      </Modal>
      {error && (
        <ErrorPaymentModal
          message={error}
          onClearError={() => setErrorMessage(null)}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default PayFromKioskModal;
